import * as React from "react";
import { Slider } from "@teliaee/react-slick";
import clsx from "clsx";
import { useMediaQuery } from "react-responsive";
import { Button } from "../../../button/button.mjs";
import "debounce";
import "tabbable";
import { useIsMounted } from "../../../helpers/helper/hooks/use-is-mounted.mjs";
import { replaceParams } from "../../../helpers/label-provider/label-provider.mjs";
import { useLabels } from "../../../helpers/label-provider/use-labels.mjs";
import { media } from "../../../helpers/theme-provider/theme-provider.mjs";
import { CarouselHeroItem } from "../carousel-hero-item/carousel-hero-item.mjs";
import { CarouselHeroContext } from "./carousel-hero-context.mjs";
const CarouselHero = (props) => {
  const [theme, setTheme] = React.useState(null);
  const labels = useLabels();
  const containerRef = React.useRef(null);
  const slickRef = React.useRef(null);
  const isMounted = useIsMounted();
  const [nextSlideIndex, setNextSlideIndex] = React.useState(0);
  const [prevSlideIndex, setPrevSlideIndex] = React.useState(0);
  const [autoplay, setAutoplay] = React.useState(true);
  const isMobile = useMediaQuery({ maxWidth: media.width.md.max });
  const handleFocusChange = React.useCallback(() => {
    var _a, _b;
    const containerElement = containerRef.current;
    const focusedElement = containerElement == null ? void 0 : containerElement.querySelector(".slick-current :focus");
    const focusedIndicatorElement = containerElement == null ? void 0 : containerElement.querySelector(".carousel-hero__indicator:focus");
    const hasFocusedElement = !!focusedElement;
    const hasFocusedIndicatorElement = !!focusedIndicatorElement;
    if (hasFocusedElement || hasFocusedIndicatorElement) {
      (_a = slickRef.current) == null ? void 0 : _a.slickPause();
    } else if (autoplay) {
      (_b = slickRef.current) == null ? void 0 : _b.slickPlay();
    }
  }, [autoplay]);
  React.useEffect(() => {
    document.addEventListener("focus", handleFocusChange, true);
    return () => {
      document.removeEventListener("focus", handleFocusChange, true);
    };
  }, [handleFocusChange]);
  const getChildren = (children) => {
    const childrenArray = React.Children.toArray(children).filter((child) => {
      return React.isValidElement(child) && child.type === CarouselHeroItem;
    });
    return childrenArray.sort((a, b) => {
      const getChildPriority = (child) => {
        var _a, _b;
        return Math.min((_b = (_a = child.props) == null ? void 0 : _a.priority) != null ? _b : 3, 3);
      };
      return getChildPriority(a) - getChildPriority(b);
    });
  };
  const getSliderOptions = () => {
    const { indicatorButtonLabel } = props;
    const autoplaySpeed = 8e3;
    const speed = 300;
    return {
      afterChange: () => {
        setPrevSlideIndex(null);
        handleFocusChange();
      },
      arrows: false,
      autoplay: true,
      autoplaySpeed,
      beforeChange: ({}, nextSlide) => {
        setNextSlideIndex(nextSlide);
        setPrevSlideIndex(nextSlideIndex);
      },
      customPaging: (index) => {
        const params = {
          index: "".concat(index + 1)
        };
        return /* @__PURE__ */ React.createElement("button", { type: "button", className: "carousel-hero__indicator" }, /* @__PURE__ */ React.createElement("span", { className: "sr-only" }, indicatorButtonLabel ? replaceParams(indicatorButtonLabel, params) : labels.get("carousel.indicatorButtonLabel", params)));
      },
      dots: true,
      dotsClass: "carousel-hero__dots",
      draggable: false,
      easing: "cubic-bezier(0.25, 0.1, 0.25, 1.000)",
      fade: true,
      pauseOnHover: true,
      pauseOnFocus: true,
      speed,
      waitForAnimate: false
    };
  };
  const mapChildren = () => {
    const children = getChildren(props.children);
    return children.map((child, index) => {
      return React.cloneElement(child, {
        isCurrent: index === nextSlideIndex,
        isNext: index > nextSlideIndex,
        isPrev: index < nextSlideIndex,
        isPrevCurrent: index === prevSlideIndex && index !== nextSlideIndex,
        minHeight: isMobile ? 0 : props.minHeight
      });
    });
  };
  const handleCarouselAutoplay = () => {
    setAutoplay((prevAutoplay) => {
      var _a, _b;
      const newAutoplay = !prevAutoplay;
      if (newAutoplay) {
        (_a = slickRef.current) == null ? void 0 : _a.slickPlay();
      } else {
        (_b = slickRef.current) == null ? void 0 : _b.slickPause();
      }
      return newAutoplay;
    });
  };
  const handleOnHover = (isHovering) => {
    var _a, _b;
    if (isHovering) {
      (_a = slickRef.current) == null ? void 0 : _a.slickPause();
    } else if (autoplay) {
      (_b = slickRef.current) == null ? void 0 : _b.slickPlay();
    }
  };
  const shouldUseSlick = isMounted && React.Children.count(props.children) > 1;
  const CarouselHeroTag = shouldUseSlick ? Slider : "div";
  return /* @__PURE__ */ React.createElement(CarouselHeroContext.Provider, { value: { theme, setTheme } }, /* @__PURE__ */ React.createElement("div", { className: clsx("carousel-hero__container", props.containerClassName), ref: containerRef, "data-theme": theme, style: props.style }, shouldUseSlick && /* @__PURE__ */ React.createElement("div", { className: "carousel-hero__pause-button-container" }, /* @__PURE__ */ React.createElement("div", { className: "carousel-hero__pause-button-inner" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      variant: "secondary",
      ...props.pauseButton,
      text: autoplay ? labels.get("pause") : labels.get("play"),
      icon: autoplay ? "#pause" : "#play",
      onClick: handleCarouselAutoplay,
      className: "carousel-hero__pause-button",
      size: "small"
    }
  ))), /* @__PURE__ */ React.createElement("div", { onMouseEnter: () => handleOnHover(true), onMouseLeave: () => handleOnHover(false), ref: containerRef }, /* @__PURE__ */ React.createElement(
    CarouselHeroTag,
    {
      className: clsx(
        "carousel-hero",
        {
          ["carousel-hero--min-height"]: !!props.minHeight
        },
        props.className
      ),
      ref: (slider) => {
        if (!(slider instanceof HTMLDivElement)) {
          slickRef.current = slider;
        }
      },
      ...shouldUseSlick && getSliderOptions(),
      style: {
        minHeight: isMobile ? "0" : props.minHeight + "px"
      }
    },
    mapChildren()
  ))));
};
export {
  CarouselHero
};
